import { AioBreadcrumbsComponent, AioButtonComponent } from '@aio/ui';
import { Component, OnInit, OnDestroy, inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { LeadsService } from '../../core/services/leads.service';
import { ContactsService } from '../../core/services/contacts.service';
import { UserService } from '../../core/services/user.service';
import { PermissionService } from '../../core/services/permission.service';

import { ReferenceDataService } from '../../core/services/reference-data.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Lead, LeadUpdateRequest } from '../../core/models/lead.model';
import { UserByPermission } from '../../core/models/user.models';
import { SYSTEM_PERMISSION_LIST } from '../../shared/constants/permissions.constants';
import { NotesComponent } from '../../shared/components/notes';
import { TabsComponent } from '../../shared/components/tabs';
import { TabItem } from '../../core/models/tab.models';
import { LeadsDocumentsTab } from './leads-documents-tab/leads-documents-tab.component';
import { LeadsHistoryTabComponent } from './leads-history-tab/leads-history-tab.component';
import { LeadDetails } from '../../core/models/lead-details.model';
import { ProjectType, LeadOrigin, LabelTag, TemperatureType } from '../../core/models/reference-data.model';
import { ReferralChannel } from '../../core/models/reference-data.model';
import { ENTITY_TYPES } from '../../shared/constants/entity-types.constants';
import { extractTenantIdFromRoute, buildRouteWithTenant } from '../../shared/utils/route.util';
import { BreadcrumbItem } from '../../core/models/breadcrumb.model';
import { getCountryCode, DEFAULT_API_PHONE_LINE_COUNTRY_CODE } from '../../shared/constants/countries.constants';
import { extensionForWorkPhoneType } from '../../shared/utils/phone-number.util';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from '../../core/models/confirmation-modal.models';
import { LeadConversionRequest } from '../../core/models/lead.model';
import { ActionFeedbackService } from '../../core/services/action-feedback.service';
import { ActionResult } from '../../core/models/action-result-toolbar.models';
import { presentActionResultWithActionFeedback } from '../../shared/utils/action-result-feedback.util';
import { ActionResultQueryParamsService } from '../../core/services/action-result-query-params.service';
import { CustomLabelModalComponent } from '../../shared/components/custom-label-modal';
import { CustomLabel } from '../../core/models/select.models';
import { NavigationGuard, NavigationGuardService } from '../../core/services/navigation-guard.service';
import { CustomAttribute, CustomAttributeDefinition } from '../../core/models/endeavor.models';
import { Activity } from '../../shared/components/activity-tab/activity/activity';
import { LeadOverviewCardComponent } from '../../shared/components/lead-overview-card/lead-overview-card.component';
import { ContactInformationCardComponent } from '../../shared/components/contact-information-card/contact-information-card.component';
import { LeadDetailsCardComponent } from '../../shared/components/lead-details-card/lead-details-card.component';
import { EntityLoadErrorStateComponent } from '../../shared/components/entity-load-error-state/entity-load-error-state.component';
@Component({
  selector: 'app-leads-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NotesComponent,
    TabsComponent,
    LeadsDocumentsTab,
    LeadsHistoryTabComponent,
    AioBreadcrumbsComponent,
    AioButtonComponent,
    ConfirmationModalComponent,
    CustomLabelModalComponent,
    Activity,
    LeadOverviewCardComponent,
    ContactInformationCardComponent,
    LeadDetailsCardComponent,
    EntityLoadErrorStateComponent,
  ],

  templateUrl: './leads-view.component.html',
  styleUrls: ['./leads-view.component.scss']
})
export class LeadsViewComponent implements OnInit, OnDestroy {
  @ViewChild(NotesComponent) notesComponent!: NotesComponent;
  @ViewChild(LeadsDocumentsTab) documentsTabComponent!: LeadsDocumentsTab;
  @ViewChild('activityRef') activityComponent!: Activity;

  private readonly permissionService = inject(PermissionService);
  private readonly navigationGuardService = inject(NavigationGuardService);
  private readonly actionFeedback = inject(ActionFeedbackService);

  // Permission checking methods
  hasConvertToOpportunityPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_CONVERT_TO_OPPORTUNITY]);
  }

  hasLeadsAddEditPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_ADD_EDIT]);
  }

  hasLeadsArchivePermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_ARCHIVE]);
  }

  hasNotesPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_NOTES_ADD_EDIT_DELETE]);
  }

  hasDocumentsPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_DOCUMENTS_ADD_EDIT_DELETE]);
  }

  hasLeadsScheduleCreatePermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.LEADS_SCHEDULES_CREATE]);
  }

  /**
   * Get FAB icon based on selected tab
   */
  getFABIcon(): string {
    if (this.selectedTab === 'documents') {
      return 'assets/icons/upload.svg';
    }
    return 'assets/icons/add_white.svg';
  }

  /**
   * Determine if the floating action button should be visible
   */
  shouldShowFAB(): boolean {
    // Hide FAB on history tab – no creation action there
    if (this.selectedTab === 'history') {
      return false;
    }
    // Hide FAB on notes tab – notes component has its own sidebar button
    if (this.selectedTab === 'notes') {
      return false;
    }
    return true;
  }

  /**
   * Handle FAB click based on the active tab
   */
  onFloatingButtonClick(): void {
    if (this.selectedTab === 'notes') {
      if (this.notesComponent && this.hasNotesPermission()) {
        this.notesComponent.addNote();
      }
      return;
    }

    if (this.selectedTab === 'documents') {
      if (this.documentsTabComponent && this.hasDocumentsPermission()) {
        this.documentsTabComponent.openUploadModal();
      }
      return;
    }

    // Activity tab – open Add Activity modal via activity component
    if (this.selectedTab === 'activity') {
      if (this.activityComponent && this.hasLeadsScheduleCreatePermission()) {
        this.activityComponent.openAddActivity();
      }
      return;
    }
  }

  userName: string = '';
  userInitials: string = '';
  isFlagged = false;
  showSuccessToast = false;
  successMessage = '';

  loading = false;
  error = '';

  // Menu toggle state
  isMenuExpanded = false;



  // Confirmation modal state
  showConvertConfirmationModal = false;
  convertConfirmationConfig: ConfirmationModalConfig = {
    title: "Convert Contact from Lead to Opportunity",
    subtitle:
      "Converting a lead moves it into your Opportunities pipeline where you can track progress, schedule meetings, and manage documents.",
    description: "",
    showInfoWarning: false,
    confirmButtonText: "Yes, Convert to Opportunity",
    icon: "assets/icons/swap.svg",
    autoCloseOnConfirm: false, // Disable auto-close to prevent multiple events
    smallButtonFont: true
  };

  // Archive confirmation modal state
  isArchiveModalOpen = false;
  archiveModalConfig: ConfirmationModalConfig = {
    title: 'You\'re About to Archive this Lead',
    subtitle:
      'Once archived, they will be hidden from the table by default. They will still be available for viewing from the filters panel.',
    description: '',
    showInfoWarning: false,
    aioDestructive: true,
    confirmButtonText: 'Yes, Archive',
    cancelButtonText: 'Cancel',
    smallButtonFont: true
  };

  // Discard changes confirmation modal state and configisDiscardModalOpen = false;
  isDiscardModalOpen = false;
  discardChangesModalConfig: ConfirmationModalConfig = {
    icon: 'assets/icons/replay.svg',
    title: 'Go Back and Discard Changes?',
    subtitle: 'You’ve made changes to the lead. If you go back now, all edits will be lost.',
    description: '',
    showInfoWarning: false,
    confirmButtonText: 'Yes, Discard Changes',
    cancelButtonText: 'Cancel'
  };

  // Flag to prevent multiple API calls
  isConvertingLead = false;

  // Flag to start edit mode once users are loaded
  pendingEditMode = false;

  // Tab configuration
  selectedTab: string = 'activity';
  activitySearchTerm: string = '';
  activityInitialDate: string | null = null; // Date to navigate to when activity tab opens (e.g. from calendar Go To)
  activityInitialEditScheduleId: number | null = null; // Schedule to open in edit mode (e.g. from calendar Edit)
  showActivitySearchBar: boolean = false;
  notesSearchTerm: string = '';
  showNotesSearchBar: boolean = false;
  documentsSearchTerm: string = '';
  showDocumentsSearchBar: boolean = false;
  historySearchTerm: string = '';
  showHistorySearchBar: boolean = false;
  tabs: TabItem[] = [
    {
      id: 'activity',
      title: 'Activity',
      icon: 'assets/icons/calendar_blue.svg'
    },
    {
      id: 'notes',
      title: 'Notes',
      icon: 'assets/icons/notes.svg'
    },
    {
      id: 'documents',
      title: 'Files',
      icon: 'assets/icons/documents.svg'
    },
    {
      id: 'history',
      title: 'History',
      icon: 'assets/icons/activity.svg',
      count: undefined
    }
  ];

  // Store original data for undo functionality
  originalLeadDetails: LeadDetails | null = null;

  // Reference data for dropdowns
  projectTypes: ProjectType[] = [];
  leadOrigins: LeadOrigin[] = [];
  referralChannels: ReferralChannel[] = [];
  labelTags: LabelTag[] = [];
  temperatureTypes: TemperatureType[] = [];
  users: UserByPermission[] = [];

  // Tab management methods
  onTabSelected(tabId: string): void {
    // Close search bars when switching tabs
    this.showActivitySearchBar = false;
    this.showNotesSearchBar = false;
    this.showDocumentsSearchBar = false;
    this.showHistorySearchBar = false;
    this.selectedTab = tabId;
  }

  onMenuToggle(expanded: boolean): void {
    this.isMenuExpanded = expanded;
  }

  onSearchToggle(): void {
    // Handle search toggle based on current tab
    if (this.selectedTab === 'activity') {
      // Show the search bar and focus it after a brief delay
      this.showActivitySearchBar = true;
      setTimeout(() => {
        // The search bar will be focused by the tabs component
      }, 100);
    } else if (this.selectedTab === 'notes') {
      // Show the notes search bar and focus it after a brief delay
      this.showNotesSearchBar = true;
      setTimeout(() => {
        // The search bar will be focused by the tabs component
      }, 100);
    } else if (this.selectedTab === 'documents') {
      // Show the documents search bar and focus it after a brief delay
      this.showDocumentsSearchBar = true;
      setTimeout(() => {
        // The search bar will be focused by the tabs component
      }, 100);
    } else if (this.selectedTab === 'history') {
      // Show the history search bar and focus it after a brief delay
      this.showHistorySearchBar = true;
      setTimeout(() => {
        // The search bar will be focused by the tabs component
      }, 100);
    } else {
      // Switch to activity tab if not already selected (default behavior)
      this.selectedTab = 'activity';
      this.showActivitySearchBar = true;
      setTimeout(() => {
        // The search bar will be focused by the tabs component
      }, 100);
    }
  }

  onSearchClose(): void {
    // Hide the search bar and clear the search term based on current tab
    if (this.selectedTab === 'activity') {
      this.showActivitySearchBar = false;
      this.activitySearchTerm = '';
    } else if (this.selectedTab === 'notes') {
      this.showNotesSearchBar = false;
      this.notesSearchTerm = '';
    } else if (this.selectedTab === 'documents') {
      this.showDocumentsSearchBar = false;
      this.documentsSearchTerm = '';
    } else if (this.selectedTab === 'history') {
      this.showHistorySearchBar = false;
      this.historySearchTerm = '';
    }
  }

  onActivitySearchChange(value: string): void {
    this.activitySearchTerm = value;
  }

  onNotesSearchChange(value: string): void {
    this.notesSearchTerm = value;
  }

  onDocumentsSearchChange(value: string): void {
    this.documentsSearchTerm = value;
  }

  onHistorySearchChange(value: string): void {
    this.historySearchTerm = value;
  }

  // Lead ID from route parameters
  currentLeadId: number | null = null;



  // Breadcrumbs for the page
  leadBreadcrumbs: BreadcrumbItem[] = [];
  private readonly leadsService = inject(LeadsService);
  private readonly contactsService = inject(ContactsService);
  private readonly userService = inject(UserService);


  private pendingRoute: string[] | null = null;
  private pendingQueryParams: any = null;
  private readonly navigationGuard: NavigationGuard = {
    hasUnsavedChanges: () => this.isEditingLeadDetails === true,
    onNavigationAttempt: (route: string[], queryParams?: any) => {
      this.pendingRoute = route;
      this.pendingQueryParams = queryParams || null;
      this.isDiscardModalOpen = true;
    }
  };

  private readonly referenceDataService = inject(ReferenceDataService);
  private readonly cd = inject(ChangeDetectorRef);

  customFields: CustomAttribute[] = [];
  loadingCustomFields = false;
  customAttributeDefinitions: CustomAttributeDefinition[] = [];
  loadingCustomAttributeDefinitions = false;

  currentLead: Lead | undefined;
  participants: Lead['participants'] = [];

  // Inline edit properties
  isEditingLeadDetails = false;
  isEditingParticipants = false;

  // Delete confirmation modal for participants
  showDeleteModal = false;
  deleteModalConfig: ConfirmationModalConfig = {
    title: 'Delete Contact',
    description: 'Are you sure you want to delete this contact? This action cannot be undone.',
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonDisabled: false,
    loading: false
  };
  participantToDelete: { index: number; participantId: number; name: string } | null = null;

  // Delete confirmation modal for lead
  showDeleteLeadModal = false;
  deleteLeadModalConfig: ConfirmationModalConfig = {
    title: 'Confirm Deletion of Lead',
    subtitle: 'This action will permanently remove the lead and its contents.',
    description: '',
    showInfoWarning: false,
    aioDestructive: true,
    confirmButtonText: 'Yes, Delete',
    cancelButtonText: 'Cancel'
  };

  // Custom label modal
  showCustomLabelModal = false;
  creatingLabel = false;

  leadDetails: LeadDetails = {
    // Core lead information
    title: '',
    description: '',
    projectValue: 0,
    leadOrigin: '',
    projectType: '',

    // Address information
    street1: '',
    city: '',
    state: '',
    zip: '',
    country: '',

    // Additional lead fields
    leadOriginId: '',
    referralSource: '',
    temperatureTypeId: '',
    label: '',
    owner: ''
  };

  startEditLeadDetails() {
    if (!this.currentLead) return;

    // If users are not loaded yet, wait for them
    if (!this.users || this.users.length === 0) {
      // Set a flag to start edit mode once users are loaded
      this.pendingEditMode = true;
      return;
    }

    this.isEditingLeadDetails = true;

    // Store original data for undo functionality
    this.originalLeadDetails = {
      // Core lead information
      title: this.currentLead.title || '',
      description: this.currentLead.description || '',
      projectValue: this.currentLead.projectValue || 0,
      leadOrigin: this.getLeadOriginDisplay(this.currentLead.leadOriginId),
      projectType: this.mapProjectTypeId(this.currentLead.projectTypeId),

      // Address information
      street1: this.currentLead.street1 || '',
      city: this.currentLead.city || '',
      state: this.currentLead.state || '',
      zip: this.currentLead.zip || '',
      country: this.currentLead.country || '',

      // Additional lead fields
      leadOriginId: this.currentLead.leadOriginId ? this.currentLead.leadOriginId.toString() : '',
      referralSource: this.currentLead.referralChannelId ? this.currentLead.referralChannelId.toString() : '',
      temperatureTypeId: this.currentLead.temperatureTypeId ? this.currentLead.temperatureTypeId.toString() : '',
      label: this.getLabelsForForm(this.currentLead.labels),
      owner: this.getProjectOwnerIdForForm()
    };

    this.leadDetails = { ...this.originalLeadDetails };

  }

  hideSuccessToast(): void {
    this.showSuccessToast = false;
    this.successMessage = '';
  }

  cancelEditLeadDetails() {
    this.isEditingLeadDetails = false;
  }

  // Unified edit methods
  startUnifiedEdit() {
    // Check if user has permission to edit leads
    if (!this.hasLeadsAddEditPermission()) {
      return;
    }

    this.isEditingLeadDetails = true;
    this.isEditingParticipants = true;
  }

  /**
   * Map phone numbers from form structure to API structure for participant updates
   * Always maps phone numbers to their correct positions and clears unused positions
   */
  private mapPhoneNumbersToParticipantAPI(phoneNumbers: any[]): any {
    const result: any = {};

    // Define all 5 phone number field mappings
    // Note: First phone uses tempCountryCode (participant's address country code)
    // Secondary through fifth phones have their own country code fields
    const phoneFields = [
      {
        form: 'tempPhoneNumber',
        extension: 'tempPhoneExtension',
        type: 'tempPhoneNumberType',
        countryCode: null // First phone uses tempCountryCode (participant's address country)
      },
      {
        form: 'tempSecondaryPhoneNumber',
        extension: 'tempSecondaryPhoneExtension',
        type: 'tempSecondaryPhoneNumberType',
        countryCode: 'tempSecondaryPhoneCountryCode'
      },
      {
        form: 'tempThirdPhoneNumber',
        extension: 'tempThirdPhoneExtension',
        type: 'tempThirdPhoneNumberType',
        countryCode: 'tempThirdPhoneCountryCode'
      },
      {
        form: 'tempFourthPhoneNumber',
        extension: 'tempFourthPhoneExtension',
        type: 'tempFourthPhoneNumberType',
        countryCode: 'tempFourthPhoneCountryCode'
      },
      {
        form: 'tempFifthPhoneNumber',
        extension: 'tempFifthPhoneExtension',
        type: 'tempFifthPhoneNumberType',
        countryCode: 'tempFifthPhoneCountryCode'
      }
    ];

    // First, clear all phone number fields
    phoneFields.forEach(field => {
      result[field.form] = '';
      result[field.extension] = '';
      result[field.type] = '';
      // Clear country code fields for secondary through fifth phones
      if (field.countryCode) {
        result[field.countryCode] = '';
      }
    });

    // Then, map all phone numbers in the array (including empty ones)
    phoneNumbers.forEach((phone, index) => {
      if (index < phoneFields.length) {
        const field = phoneFields[index];
        const typeVal =
          phone.phoneType !== undefined && phone.phoneType !== null && String(phone.phoneType).trim() !== ''
            ? String(phone.phoneType).trim()
            : String(phone.phoneNumberType ?? '').trim();
        result[field.form] = phone.phoneNumber || '';
        result[field.extension] = extensionForWorkPhoneType(typeVal, phone.phoneExtension || '');
        // Preserve phone type value, even if empty (don't default to 'cell')
        result[field.type] = typeVal;

        const phoneDigits = (phone.phoneNumber || '').toString().replace(/\D/g, '').trim();
        if (field.countryCode && phoneDigits) {
          result[field.countryCode] = DEFAULT_API_PHONE_LINE_COUNTRY_CODE;
        }
      }
    });

    return result;
  }

  /**
   * Map phone numbers array to contact API format (single phoneNumber/phoneExtension)
   * Returns the first complete phone number, or null if none exist
   */
  private mapPhoneNumbersToContactAPI(phoneNumbers: any[]): { phoneNumber: string | null, phoneExtension: string | null, countryCode: string | null } {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      return { phoneNumber: null, phoneExtension: null, countryCode: null };
    }

    // Find first complete phone number (all fields must have values)
    const firstCompletePhone = phoneNumbers.find((phone: any) => {
      const phoneTypeStr = (phone.phoneType ? String(phone.phoneType).trim() : '') || (phone.phoneNumberType ? String(phone.phoneNumberType).trim() : '');
      const phoneNumberStr = phone.phoneNumber ? String(phone.phoneNumber).trim() : '';
      return phoneTypeStr !== '' && phoneNumberStr !== '';
    });

    if (!firstCompletePhone) {
      return { phoneNumber: null, phoneExtension: null, countryCode: null };
    }

    const typeStr =
      (firstCompletePhone.phoneType ? String(firstCompletePhone.phoneType).trim() : '') ||
      (firstCompletePhone.phoneNumberType ? String(firstCompletePhone.phoneNumberType).trim() : '');
    const ext = extensionForWorkPhoneType(typeStr, firstCompletePhone.phoneExtension);

    return {
      phoneNumber: firstCompletePhone.phoneNumber || null,
      phoneExtension: ext,
      countryCode: DEFAULT_API_PHONE_LINE_COUNTRY_CODE
    };
  }



  private actionResultQueryParamsSubscription: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private actionResultQueryParamsService: ActionResultQueryParamsService
  ) {
    this.route.params.subscribe(params => {
      if (params['id']) {
        const leadId = parseInt(params['id']);
        this.currentLeadId = leadId; // Store the lead ID immediately
        // Set initial breadcrumb immediately
        this.setLeadViewBreadcrumbs('Loading...');
        this.loadLeadById(leadId);
      }
    });
  }

  ngOnInit(): void {
    this.loadReferenceData();
    this.loadCustomAttributeDefinitions();
    this.navigationGuardService.registerGuard(this.navigationGuard);

    // Check for action result query params (from navigation from add lead)
    this.checkForActionResultQueryParams();

    // Navigate to activity tab at specific date when coming from calendar Go To
    const q = this.route.snapshot.queryParams;
    if (q['tab'] === 'activity') {
      if (q['activityDate']) {
        this.activityInitialDate = q['activityDate'];
      }
      if (q['editScheduleId']) {
        const scheduleId = Number(q['editScheduleId']);
        if (!isNaN(scheduleId)) {
          this.activityInitialEditScheduleId = scheduleId;
        }
      }
      this.selectedTab = 'activity';
      this.showActivitySearchBar = true;
    }
  }

  /**
   * Load reference data for dropdowns
   */
  private loadReferenceData(): void {
    // Load project types
    this.referenceDataService.getProjectTypes().subscribe({
      next: (projectTypes) => {
        this.projectTypes = projectTypes;
      },
      error: (error) => {
        // Handle error silently
      }
    });

    // Load lead origins
    this.referenceDataService.getLeadOrigins().subscribe({
      next: (leadOrigins) => {
        this.leadOrigins = leadOrigins;
      },
      error: (error) => {
        // Handle error silently
      }
    });

    // Load referral channels
    this.referenceDataService.getReferralChannels().subscribe({
      next: (referralChannels) => {
        this.referralChannels = referralChannels;
      },
      error: (error) => {
        // Handle error silently
      }
    });

    // Load temperature types
    this.referenceDataService.getTemperatureTypes().subscribe({
      next: (temperatureTypes) => {
        this.temperatureTypes = temperatureTypes || [];
      },
      error: (error) => {
        console.error('❌ Error loading temperature types:', error);
        this.temperatureTypes = [];
      }
    });

    // Load label tags
    this.referenceDataService.getLabelTags(ENTITY_TYPES.LEAD).subscribe({
      next: (labelTags) => {
        this.labelTags = labelTags;

        this.labelTags = labelTags || [];

        // If we have a current lead and are in edit mode, update the form
        if (this.currentLead && this.isEditingLeadDetails) {
                  this.leadDetails.label = this.getLabelsForForm(this.currentLead.labels);
        }
      },
      error: (error) => {
        this.labelTags = [];
      }
    });

    // Load users for project owner display
    const tenantId = extractTenantIdFromRoute(this.router);
    if (tenantId) {
      const leadPermissions = [
        SYSTEM_PERMISSION_LIST.LEADS_VIEW
      ];
      this.userService.getUsersByPermission(leadPermissions, tenantId).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.users = response.data;

            // Always refresh owner selection when users are loaded
            this.refreshOwnerSelection();

            // If we're already in edit mode, we need to refresh the form with the loaded users
            if (this.isEditingLeadDetails) {
              // Owner selection is already refreshed above
            }

            // If there's a pending edit mode, start it now that users are loaded
            if (this.pendingEditMode) {
              this.pendingEditMode = false;
              this.startEditLeadDetails();
            }
          } else {
            this.users = [];
          }
        },
        error: (error) => {
          this.users = [];
        }
      });
    } else {
      this.users = [];
    }
  }

  ngOnDestroy(): void {
    this.navigationGuardService.unregisterGuard(this.navigationGuard);

    if (this.actionResultQueryParamsSubscription) {
      this.actionResultQueryParamsSubscription.unsubscribe();
      this.actionResultQueryParamsSubscription = null;
    }
  }

  /**
   * Refresh owner selection when users are loaded after form initialization
   */
  private refreshOwnerSelection(): void {
    if (this.leadDetails) {
      const ownerId = this.getProjectOwnerIdForForm();
      this.leadDetails.owner = ownerId;

      // Force change detection to update the form
      this.cd.detectChanges();
    }
  }

  private loadCustomFields(lead: Lead): void {
    this.loadingCustomFields = true;
    if(lead.customAttributes && Array.isArray(lead.customAttributes)) {
      this.customFields = lead.customAttributes;
    } else {
      this.customFields = [];
    }
    this.loadingCustomFields = false;
  }

  private loadCustomAttributeDefinitions(): void {
    this.loadingCustomAttributeDefinitions = true;
    this.leadsService.getCustomAttributeDefinitions('LEAD').subscribe({
      next: (definitions) => {
        this.customAttributeDefinitions = definitions;
        this.loadingCustomAttributeDefinitions = false;
      },
      error: (error) => {
        this.customAttributeDefinitions = [];
        this.loadingCustomAttributeDefinitions = false;
      }
    });
  }

  private loadLeadById(leadId: number): void {
    this.loading = true;
    this.error = '';

    this.leadsService.getLeadById(leadId).subscribe({
      next: (lead) => {
        this.currentLead = lead;
        this.currentLeadId = leadId;

        // Load lead data

        // Initialize flag status from the lead data
        this.isFlagged = lead.isFlagged || false;

        // Set the user name and initials for display (this is actually the lead title now)
        this.userName = lead.title || `${lead.firstName} ${lead.lastName}`;
        this.userInitials = this.getInitials(this.userName);

        // Set breadcrumbs using lead title
        this.setLeadViewBreadcrumbs(lead.title || `${lead.firstName} ${lead.lastName}`);

        // Initialize lead details for editing
        this.leadDetails = {
          // Core lead information
          title: lead.title || '',
          description: lead.description || '',
          projectValue: lead.projectValue || 0,
          leadOrigin: this.getLeadOriginDisplay(lead.leadOriginId),
          projectType: this.mapProjectTypeId(lead.projectTypeId),

          // Address information
          street1: lead.street1 || '',
          city: lead.city || '',
          state: lead.state || '',
          zip: lead.zip || '',
          country: lead.country || '',

          // Additional lead fields
          leadOriginId: lead.leadOriginId ? lead.leadOriginId.toString() : '',
          referralSource: lead.referralChannelId ? lead.referralChannelId.toString() : '',
          temperatureTypeId: lead.temperatureTypeId ? lead.temperatureTypeId.toString() : '',
          label: this.getLabelsForForm(lead.labels),
          owner: this.getProjectOwnerIdForForm()
        };

        // Initialize lead details

        // Force change detection to update the view
        this.cd.detectChanges();

        // Set participants from the lead response
        // NEW FLOW: API now provides participants with isPrimary field to identify the primary contact
        // All participants (including primary) come from the participants array
        if (lead.participants && lead.participants.length > 0) {
          // Sort participants so primary participant appears first
          this.participants = lead.participants.sort((a, b) => {
            // Primary participants (isPrimary: true) should come first
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return 0; // Keep original order for non-primary participants
          });

          // Ensure all participants loaded from API have isNew: false
          this.participants = this.participants.map(participant => ({
            ...participant,
            isNew: false // Explicitly mark as existing participant
          }));
        } else {
          // Fallback: If no participants exist, create empty array
          // In the new flow, this should rarely happen as participants should always be provided
          this.participants = [];
        }

        this.loadCustomFields(lead);

        this.loading = false;
      },
      error: (error) => {
        this.error = 'Failed to load lead details';
        this.loading = false;
      }
    });
  }

  /**
   * Cancel participants edit mode
   */
  cancelEditParticipants(): void {
    this.isEditingParticipants = false;
    // Reload the lead data to discard any unsaved changes
    if (this.currentLeadId) {
      this.loadLeadById(this.currentLeadId);
    }
  }

  /**
   * Handle delete participant confirmation modal events
   */
  onDeleteParticipantConfirm(): void {
    if (this.participantToDelete) {
      this.deleteParticipant(this.participantToDelete.participantId, this.participantToDelete.index);
    }
  }

  onDeleteParticipantCancel(): void {
    this.showDeleteModal = false;
    this.participantToDelete = null;
    this.deleteModalConfig.loading = false;
  }

  /**
   * Delete participant from the API
   */
  deleteParticipant(participantId: number, index: number): void {
    // Prevent duplicate calls
    if (this.deleteModalConfig.loading) {
      return;
    }

    if (!this.currentLeadId) {
      return;
    }

    if (!participantId || participantId <= 0) {
      return;
    }

    // Set loading state to prevent duplicate calls
    this.deleteModalConfig.loading = true;

    this.leadsService.deleteParticipant(this.currentLeadId, participantId).subscribe({
      next: (response) => {
        // Remove from local array
        if (this.participants && index >= 0 && index < this.participants.length) {
          this.participants.splice(index, 1);
          this.participants = [...this.participants];
        }

        // Close modal and reset state
        this.showDeleteModal = false;
        this.participantToDelete = null;
        this.deleteModalConfig.loading = false;

        // Show success toast
        this.showSuccessToast = true;
        this.successMessage = 'Participant deleted successfully!';
        setTimeout(() => {
          this.showSuccessToast = false;
        }, 3000);
      },
      error: (error) => {
        // Reset modal state
        this.deleteModalConfig.loading = false;

        // Show error toast
        this.showSuccessToast = true;
        this.successMessage = 'Error deleting participant. Please try again.';
        setTimeout(() => {
          this.showSuccessToast = false;
        }, 3000);
      }
    });
  }

  /**
   * Get labels as array of IDs for form (multi-select expects array of values)
   */
  getLabelsForForm(labels: any): string[] {
    // Handle case where labels is null, undefined, or empty
    if (!labels) {
      return [];
    }

    // Handle case where labels is a string instead of array
    if (typeof labels === 'string') {
      return [];
    }

    // Handle case where labels is not an array
    if (!Array.isArray(labels)) {
      return [];
    }

    // Handle case where labels array is empty
    if (labels.length === 0) {
      return [];
    }

    const validLabels = labels.filter(label => {
      return label && typeof label === 'object' && (label.labelTagId !== undefined && label.labelTagId !== null);
    });

    const result = validLabels.map(label => label.labelTagId.toString());
    return result;
  }

  /**
   * Get lead address object for activity component
   */
  getLeadAddressForActivity(): {street1?: string, street2?: string, city?: string, state?: string, zip?: string} | null {
    if (!this.currentLead) {
      return null;
    }

    return {
      street1: this.currentLead.street1 || undefined,
      street2: this.currentLead.street2 || undefined,
      city: this.currentLead.city || undefined,
      state: this.currentLead.state || undefined,
      zip: this.currentLead.zip || undefined
    };
  }

  /**
   * Get the project owner ID for form editing
   */
  getProjectOwnerIdForForm(): string {
    // Check if we have projectOwners array from the backend response
    if (this.currentLead?.projectOwners && this.currentLead.projectOwners.length > 0) {
      const projectOwner = this.currentLead.projectOwners[0]; // Get the first project owner
      if (projectOwner.userId) {
        return projectOwner.userId.toString();
      }
    }

    // Fallback to old projectOwnerId logic
    if (!this.currentLead?.projectOwnerId) {
      return '';
    }
    return this.currentLead.projectOwnerId.toString();
  }

  /**
   * Map backend project type ID to frontend project type name
   */
  mapProjectTypeId(projectTypeId: number | null | undefined): string {
    if (projectTypeId == null || projectTypeId === 0) {
      return '';
    }
    if (!this.projectTypes || this.projectTypes.length === 0) {
      return '';
    }

    const projectType = this.projectTypes.find(pt => pt.projectTypeId === projectTypeId);
    return projectType ? projectType.name : '';
  }

  /**
   * Map backend lead origin ID to frontend display name
   */
  getLeadOriginDisplay(leadOriginId: number | null | undefined): string {
    // Return empty string if leadOriginId is null, undefined, or 0
    if (!leadOriginId || leadOriginId === 0) {
      return '';
    }

    if (!this.leadOrigins || this.leadOrigins.length === 0) {
      return '';
    }

    const leadOrigin = this.leadOrigins.find(lo => lo.leadOriginId === leadOriginId);
    return leadOrigin ? leadOrigin.name : '';
  }

  private isNavigating = false;

  goToLeads() {
    if (this.isNavigating) {
      return;
    }

    this.isNavigating = true;

    // Get the current URL to determine the proper navigation path
    const currentUrl = window.location.pathname;

    // Check if we're in a tenant context
    const urlParts = currentUrl.split('/');
    const tenantId = urlParts[1] && urlParts[1] !== 'leads' ? urlParts[1] : null;

    if (this.navigationGuardService.hasUnsavedChanges()) {
      this.navigationGuardService.attemptNavigation([tenantId!, 'leads']);
      return;
    }

    if (tenantId) {
      // We're in a tenant context, navigate to tenant/leads
      this.router.navigate([tenantId, 'leads']).then(() => {
        this.isNavigating = false;
      }).catch(error => {
        this.isNavigating = false;
      });
    } else {
      // We're not in a tenant context, navigate to leads
      this.router.navigate(['leads']).then(() => {
        this.isNavigating = false;
      }).catch(error => {
        this.isNavigating = false;
      });
    }
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  toggleFlag() {
    if (!this.currentLead) return;

    // Toggle the flag status
    this.isFlagged = !this.isFlagged;

    // Update the currentLead object
    this.currentLead.isFlagged = this.isFlagged;

    // Prepare update request for flag status
    const updateRequest: LeadUpdateRequest = {
      id: this.currentLead.leadId,
      // Keep existing data
      email: this.currentLead.email,
      firstName: this.currentLead.firstName,
      lastName: this.currentLead.lastName,
      city: this.currentLead.city,
      street1: this.currentLead.street1,
      street2: this.currentLead.street2,
      state: this.currentLead.state, // Keep original state value
      zip: this.currentLead.zip,
      countryCode: this.currentLead.countryCode,
      country: this.currentLead.country,
      projectTypeId: this.currentLead.projectTypeId,
      temperatureTypeId: this.currentLead.temperatureTypeId,
      leadOriginId: this.currentLead.leadOriginId,
      phoneNumber: this.currentLead.phoneNumber,
      phoneExtension: this.currentLead.phoneExtension,
      description: this.currentLead.description,
      isFlagged: this.isFlagged // Include the flag status in the update
    };

    // Call the backend API to update the lead flag status
    this.leadsService.updateLead(updateRequest).subscribe({
      next: (updatedLead) => {
        // Update the current lead with the response from backend
        this.currentLead = updatedLead;

        this.presentActionResult({
          action: this.isFlagged ? 'flag' : 'unflag',
          message: this.isFlagged ? 'Lead flagged successfully.' : 'Lead unflagged successfully.',
          timestamp: new Date(),
          isCustomMessage: true
        });
      },
      error: (error) => {
        // Revert the flag status on error
        this.isFlagged = !this.isFlagged;
        this.currentLead!.isFlagged = this.isFlagged;

        this.showSuccessToast = true;
        this.successMessage = 'Error updating flag status. Please try again.';

        // Hide error message after 3 seconds
        setTimeout(() => {
          this.hideSuccessToast();
        }, 3000);
      }
    });
  }


  archiveLead(): void {
    if (!this.currentLead || this.currentLead.isArchived) return;

    // Open archive confirmation modal
    this.isArchiveModalOpen = true;
  }

  onArchiveModalConfirm(event: any): void {
    if (!this.currentLead) {
      this.isArchiveModalOpen = false;
      return;
    }

    const updateRequest: LeadUpdateRequest = {
      id: this.currentLead.leadId,
      title: this.currentLead.title,
      email: this.currentLead.email,
      firstName: this.currentLead.firstName,
      middleName: this.currentLead.middleName,
      lastName: this.currentLead.lastName,
      city: this.currentLead.city,
      street1: this.currentLead.street1,
      street2: this.currentLead.street2,
      state: this.currentLead.state,
      zip: this.currentLead.zip,
      countryCode: this.currentLead.countryCode,
      country: this.currentLead.country,
      projectTypeId: this.currentLead.projectTypeId,
      projectTypeIds: this.currentLead.projectTypeIds,
      temperatureTypeId: this.currentLead.temperatureTypeId,
      leadOriginId: this.currentLead.leadOriginId,
      referralChannelId: this.currentLead.referralChannelId ?? undefined,
      phoneNumber: this.currentLead.phoneNumber,
      phoneExtension: this.currentLead.phoneExtension,
      description: this.currentLead.description,
      projectValue: this.currentLead.projectValue,
      projectOwnerId: this.currentLead.projectOwnerId,
      isFlagged: this.currentLead.isFlagged,
      isArchived: true
    };

    if (this.currentLead.customAttributes?.length) {
      updateRequest.customAttributes = this.currentLead.customAttributes;
    }

    this.leadsService.updateLead(updateRequest).subscribe({
      next: (updatedLead) => {
        this.currentLead = updatedLead;
        this.isArchiveModalOpen = false;
      },
      error: (error) => {
        console.error('Error archiving lead:', error);
        this.isArchiveModalOpen = false;
        this.showSuccessToast = true;
        this.successMessage = 'Error archiving lead. Please try again.';
        setTimeout(() => {
          this.showSuccessToast = false;
        }, 3000);
      }
    });
  }

  onArchiveModalCancel(event: any): void {
    this.isArchiveModalOpen = false;
  }

  // Breadcrumb methods for leads
  setLeadViewBreadcrumbs(leadName: string) {
    const tenantId = extractTenantIdFromRoute(this.router);
    if (tenantId) {
      const leadsRoute = `/${tenantId}/leads`;
      this.leadBreadcrumbs = [
        {
          label: 'Leads',
          route: leadsRoute
        },
        {
          label: leadName,
          plain: true
        }
      ];
    }
  }

  onBreadcrumbClick(item: BreadcrumbItem) {
    if (item.route) {
      if (this.navigationGuardService.hasUnsavedChanges()) {
        this.navigationGuardService.attemptNavigation([item.route]);
        return;
      }
      this.router.navigate([item.route]);
    }
  }

  // Convert to Opportunity methods
  openConvertConfirmationModal(): void {
    // Check if user has permission to convert leads to opportunities
    if (!this.hasConvertToOpportunityPermission()) {
      return;
    }

    // Prevent opening multiple modals
    if (this.showConvertConfirmationModal || this.isConvertingLead) {
      return;
    }
    this.showConvertConfirmationModal = true;
  }

  closeConvertConfirmationModal(): void {
    this.showConvertConfirmationModal = false;
    // Reset conversion flag if modal is closed without conversion
    if (!this.isConvertingLead) {
      this.isConvertingLead = false;
    }
  }

  onConvertConfirm(): void {
    if (!this.currentLead?.leadId) {
      return;
    }

    // Prevent multiple API calls
    if (this.isConvertingLead) {
      return;
    }

    this.isConvertingLead = true;

    // Prepare the conversion request
    const conversionRequest: LeadConversionRequest = {
      leadId: this.currentLead.leadId,
      dealAmount: this.currentLead.projectValue || 0,
      dealProbability: 100, // Default to 100% probability
      opportunityStatus: 'Open' // First stage of the pipeline
    };

    // Call the API to convert the lead to opportunity
    this.leadsService.convertLeadToOpportunity(conversionRequest).subscribe({
      next: (response) => {
        // Close the modal
        this.closeConvertConfirmationModal();

        // Reset the conversion flag
        this.isConvertingLead = false;

        // Navigate to the new opportunity detail view with success message in query params
        const opportunityId = response?.data?.opportunity?.opportunityId;

        if (this.navigationGuardService.hasUnsavedChanges()) {
          this.navigationGuardService.attemptNavigation([opportunityId!.toString()]);
          return;
        }

        if (opportunityId) {
          const opportunityRoute = buildRouteWithTenant(this.router, ['opportunities', 'view', opportunityId.toString()]);
          this.router.navigate(opportunityRoute, {
            queryParams: {
              actionResult: 'convert',
              message: 'Lead converted to opportunity successfully.',
              count: 1,
              itemLabel: 'lead',
              actionPastTense: 'converted'
            }
          });
        } else {
          const opportunitiesRoute = buildRouteWithTenant(this.router, ['opportunities']);
          this.router.navigate(opportunitiesRoute, {
            queryParams: {
              actionResult: 'convert',
              message: 'Lead converted to opportunity successfully.',
              count: 1,
              itemLabel: 'lead',
              actionPastTense: 'converted'
            }
          });
        }
      },
      error: (error) => {

        this.presentActionResult({
          action: 'error',
          message: 'Error converting lead to opportunity. Please try again.',
          timestamp: new Date(),
          isCustomMessage: true,
          isError: true
        });

        // Reset the conversion flag on error
        this.isConvertingLead = false;

        // Keep modal open on error so user can try again
      }
    });
  }

  onConvertCancel(): void {
    this.closeConvertConfirmationModal();
  }

  onDeleteLeadConfirm(): void {
    if (!this.currentLead?.leadId) {
      return;
    }

    // Set loading state
    this.deleteLeadModalConfig.loading = true;

    this.leadsService.deleteLead(this.currentLead.leadId).subscribe({
      next: () => {
        // Close modal
        this.showDeleteLeadModal = false;
        this.deleteLeadModalConfig.loading = false;

        // Navigate to leads list with success message in query params
        // This will trigger the floating toolbar to show on the leads page
        const leadsRoute = buildRouteWithTenant(this.router, ['leads']);
        this.router.navigate(leadsRoute, {
          queryParams: {
            actionResult: 'delete',
            message: 'Lead deleted successfully.',
            count: 1,
            itemLabel: 'lead',
            actionPastTense: 'deleted'
          }
        });
      },
      error: (error) => {

        // Reset loading state
        this.deleteLeadModalConfig.loading = false;

        this.presentActionResult({
          action: 'error',
          message: 'Error deleting lead. Please try again.',
          timestamp: new Date(),
          isCustomMessage: true,
          isError: true
        });
      }
    });
  }

  onDeleteLeadCancel(): void {
    this.showDeleteLeadModal = false;
    this.deleteLeadModalConfig.loading = false;
  }

  onDiscardConfirm(): void {
    this.isDiscardModalOpen = false;
    this.discardChangesModalConfig.loading = false;
    this.cancelEditLeadDetails();
    this.cancelEditParticipants();

    if (this.pendingRoute) {
      this.router.navigate(this.pendingRoute);
    }
  }

  onDiscardCancel(): void {
    this.isDiscardModalOpen = false;
    this.discardChangesModalConfig.loading = false;
  }

  onSaveCustomLabel(label: CustomLabel): void {
    // Set loading state
    this.creatingLabel = true;

    // Create the label tag using the API
    const createRequest = {
      name: label.name,
      labelType: 'custom', // Default label type for custom labels
      colorCode: label.color,
      entityType: ENTITY_TYPES.LEAD,
    };

    this.referenceDataService.createLabelTag(createRequest).subscribe({
      next: (createdLabelTag) => {
        this.creatingLabel = false;
        this.showCustomLabelModal = false;

        // Refresh the label tags to include the newly created one
        this.refreshLabelTags();

        // Show success message
        this.showSuccessToast = true;
        this.successMessage = 'Custom label created successfully!';
        setTimeout(() => {
          this.hideSuccessToast();
        }, 3000);
      },
      error: (error) => {
        this.creatingLabel = false;
        this.error = 'Failed to create label tag. Please try again.';
      }
    });
  }

  onCancelCustomLabel(): void {
    this.showCustomLabelModal = false;
  }

  private refreshLabelTags(): void {
    this.referenceDataService.getLabelTags(ENTITY_TYPES.LEAD).subscribe({
      next: (labelTags) => {
        this.labelTags = labelTags;
      },
      error: (error) => {
      }
    });
  }

  // Check for action result query params (from navigation from add lead)
  private checkForActionResultQueryParams(): void {
    this.actionResultQueryParamsSubscription = this.actionResultQueryParamsService
      .checkForActionResultQueryParams(this.route, 'lead', 'created')
      .subscribe(result => {
        if (result) {
          this.presentActionResult(result);
        }
      });
  }

  // Card action handlers
  onCardEditClick(cardType: string): void {
    // For overview card, let it handle its own inline editing
    // For other cards, trigger the unified edit mode
    if (cardType === 'overview') {
      // The card will handle its own editing state
      return;
    }
    // Trigger the unified edit mode for other cards
    if (!this.hasLeadsAddEditPermission()) {
      return;
    }
    this.startUnifiedEdit();
  }

  onCardExpandClick(cardType: string): void {
    // Cards now handle their own expansion locally
    // This method is kept for compatibility but doesn't need to do anything
  }

  onCardSaveClick(updateRequest: LeadUpdateRequest): void {
    if (!this.currentLead) return;

    // Check if this is a contact card save (which includes participant data)
    const contactCardSave = (updateRequest as any).isContactCardSave;
    const participants = (updateRequest as any).participants || [];
    const deletedParticipantIds = (updateRequest as any).deletedParticipantIds || [];

    // Extract labels if present (they need to be handled separately)
    const labelsToUpdate = updateRequest.labels;
    const updateRequestWithoutLabels = { ...updateRequest };
    delete updateRequestWithoutLabels.labels; // Remove labels from update request

    // Get current label IDs from the actual current lead data
    const currentLabelIds = this.currentLead.labels && Array.isArray(this.currentLead.labels) ?
      this.currentLead.labels.map(label => label.labelTagId).filter(id => !isNaN(id)) : [];

    // Get new label IDs from the update request labels
    const newLabelIds = labelsToUpdate && Array.isArray(labelsToUpdate) ?
      labelsToUpdate.map(label => label.labelTagId).filter(id => !isNaN(id)) : [];

    if (contactCardSave && participants.length > 0) {
      // Handle contact card save - update lead and manage participants
      this.leadsService.updateLead(updateRequestWithoutLabels).subscribe({
        next: (updatedLead) => {
          // Update the current lead with the response from backend
          this.currentLead = updatedLead;

          // Handle labels if they were updated
          if (labelsToUpdate !== undefined) {
            this.leadsService.manageLabelAssignments(
              this.currentLead.leadId,
              currentLabelIds,
              newLabelIds
            ).subscribe({
              next: () => {
                // Labels updated successfully, now save participants
                this.saveContactCardParticipants(participants, deletedParticipantIds);
              },
              error: (labelError) => {
                console.error('Error updating labels:', labelError);
                // Still save participants even if labels failed
                this.saveContactCardParticipants(participants, deletedParticipantIds);
              }
            });
          } else {
            // No labels to update, just save participants
            this.saveContactCardParticipants(participants, deletedParticipantIds);
          }
        },
        error: (error) => {
          console.error('Error updating lead:', error);
          this.presentActionResult({
            action: 'error',
            message: 'Error updating lead. Please try again.',
            timestamp: new Date(),
            isCustomMessage: true,
            isError: true
          });
        }
      });
    } else {
      // Regular card save (overview or details)
      this.leadsService.updateLead(updateRequestWithoutLabels).subscribe({
        next: (updatedLead) => {
          // Update the current lead with the response from backend
          this.currentLead = updatedLead;

          // Handle labels if they were updated
          if (labelsToUpdate !== undefined) {
            this.leadsService.manageLabelAssignments(
              this.currentLead.leadId,
              currentLabelIds,
              newLabelIds
            ).subscribe({
              next: () => {
                // Reload the lead to get updated data including labels
                this.loadLeadById(this.currentLeadId!);

                this.presentActionResult({
                  action: 'updated',
                  message: 'Lead and labels updated successfully',
                  timestamp: new Date(),
                  isCustomMessage: true
                });
              },
              error: (labelError) => {
                console.error('Error updating labels:', labelError);
                this.presentActionResult({
                  action: 'updated',
                  message: 'Lead updated but there was an issue with labels. Please try again.',
                  timestamp: new Date(),
                  isCustomMessage: true
                });
              }
            });
          } else {
            // No labels to update, reload the lead to ensure everything is in sync
            this.loadLeadById(this.currentLeadId!);

            this.presentActionResult({
              action: 'updated',
              message: 'Lead updated successfully',
              timestamp: new Date(),
              isCustomMessage: true
            });
          }
        },
        error: (error) => {
          console.error('Error updating lead:', error);
          this.presentActionResult({
            action: 'error',
            message: 'Error updating lead. Please try again.',
            timestamp: new Date(),
            isCustomMessage: true,
            isError: true
          });
        }
      });
    }
  }

  /**
   * Save participants from contact card
   * Uses the same approach as saveParticipantsUnified which was working before
   */
  private saveContactCardParticipants(participants: any[], deletedParticipantIds: number[]): void {
    if (!this.currentLeadId) return;

    // First, delete removed participants
    const deletePromises = deletedParticipantIds.map(participantId => {
      return this.leadsService.deleteParticipant(
        this.currentLeadId!,
        participantId
      ).toPromise().catch(error => {
        // Log error but don't fail the entire operation
        console.error(`Error deleting participant ${participantId}:`, error);
        return null;
      });
    });

    // Separate existing participants (to update) from new participants (to create)
    const existingParticipants = participants.filter(p => !p.isNew && p.participantId && p.participantId > 0);
    const newParticipants = participants.filter(p => p.isNew || !p.participantId || p.participantId <= 0);

    // Update existing participants - check if they have contactId to use contacts API
    const updatePromises = existingParticipants.map(participant => {
      // Check if participant has been converted to a contact (has contactId)
      if (participant.contactId && participant.contactId > 0) {
        // Use contacts API for converted contacts
        const phoneData = this.mapPhoneNumbersToContactAPI(participant.phoneNumbers || []);

        // Convert country code from full name to 2-letter code if needed
        // Only use phone's country code, no fallback to participant's country code
        let countryCode = phoneData.countryCode;
        if (countryCode) {
          if (countryCode.length > 2) {
            countryCode = getCountryCode(countryCode);
          }
        } else {
          countryCode = ''; // Don't default, use empty string
        }

        // Check if affiliation changed
        const originalAffiliation = participant.originalAffiliation || '';
        const newAffiliation = participant.affiliation || '';
        const affiliationChanged = originalAffiliation.trim() !== newAffiliation.trim();

        // Build contact update request - only include fields that have values
        const contactUpdateData: any = {
          ...(participant.firstName !== undefined && participant.firstName !== null && { firstName: participant.firstName }),
          ...(participant.lastName !== undefined && participant.lastName !== null && { lastName: participant.lastName }),
          ...(participant.email !== undefined && participant.email !== null && { email: participant.email }),
          ...(phoneData.phoneNumber !== null && { phoneNumber: phoneData.phoneNumber }),
          ...(phoneData.phoneNumber !== null && { phoneExtension: phoneData.phoneExtension ?? '' }),
          ...(countryCode && { countryCode: countryCode })
        };

        // Only include participant object if affiliation changed
        if (affiliationChanged) {
          contactUpdateData.participant = {
            participantId: participant.participantId,
            entityType: 'LEAD',
            entityId: this.currentLeadId!,
            affiliation: newAffiliation
          };
        }

        // Update contact first, then sync participant temp fields
        return this.contactsService.updateContact(participant.contactId, contactUpdateData).toPromise()
          .then(() => {
            // After contact is updated, also update the participant's temp fields
            // to ensure they sync with the contact fields
            // Convert country code from full name to code if needed
            let participantCountryCode = participant.countryCode;
            if (participantCountryCode) {
              if (participantCountryCode.length > 2) {
                participantCountryCode = getCountryCode(participantCountryCode);
              }
            } else {
              participantCountryCode = ''; // Don't default to 'US', use empty string
            }

            const participantData = {
              tempFirstName: participant.firstName,
              tempLastName: participant.lastName,
              tempEmail: participant.email,
              ...this.mapPhoneNumbersToParticipantAPI(participant.phoneNumbers || []),
              tempCity: participant.city || '', // Contact card doesn't have address fields, but include empty
              tempStreet1: participant.streetAddress || '',
              tempStreet2: participant.streetAddress2 || null,
              tempZip: participant.zipCode || '',
              tempCountryCode: participantCountryCode || '', // Use empty string instead of defaulting to 'US'
              tempCountry: participant.country || '', // Use empty string instead of defaulting to 'United States'
              isPrimary: participant.isPrimaryParticipant || false, // Use isPrimaryParticipant to match working code
              affiliation: participant.affiliation || '',
              convertToContact: true, // Keep as contact
              state: participant.state || ''
            };

            return this.leadsService.updateParticipant(
              this.currentLeadId!,
              participant.participantId,
              participantData
            ).toPromise();
          });
      } else {
        // Use participants API for unconverted participants
        // Use the existing mapPhoneNumbersToParticipantAPI method that was working
        const mappedPhoneNumbers = this.mapPhoneNumbersToParticipantAPI(participant.phoneNumbers || []);

        // Convert country code from full name to code if needed
        let countryCode = participant.countryCode;
        if (countryCode) {
          if (countryCode.length > 2) {
            countryCode = getCountryCode(countryCode);
          }
        } else {
          countryCode = ''; // Don't default to 'US', use empty string
        }

        const participantData = {
          tempFirstName: participant.firstName,
          tempLastName: participant.lastName,
          tempEmail: participant.email,
          ...mappedPhoneNumbers,
          tempCity: participant.city || '', // Contact card doesn't have address fields, but include empty
          tempStreet1: participant.streetAddress || '',
          tempStreet2: participant.streetAddress2 || null,
          tempZip: participant.zipCode || '',
          tempCountryCode: countryCode || '', // Use empty string instead of defaulting to 'US'
          tempCountry: participant.country || '', // Use empty string instead of defaulting to 'United States'
          isPrimary: participant.isPrimaryParticipant || false, // Use isPrimaryParticipant to match working code
          affiliation: participant.affiliation || '',
          convertToContact: true // Always convert to contact - matches working code
        };

        return this.leadsService.updateParticipant(
          this.currentLeadId!,
          participant.participantId,
          participantData
        ).toPromise();
      }
    });

    // Create new participants - use same format as saveParticipantsUnified
    const createPromises = newParticipants.map(participant => {
      // Use the existing mapPhoneNumbersToParticipantAPI method that was working
      const mappedPhoneNumbers = this.mapPhoneNumbersToParticipantAPI(participant.phoneNumbers || []);

      // Convert country code from full name to code if needed
      let countryCode = participant.countryCode;
      if (countryCode) {
        if (countryCode.length > 2) {
          countryCode = getCountryCode(countryCode);
        }
      } else {
        countryCode = ''; // Don't default to 'US', use empty string
      }

      const participantData = {
        tempFirstName: participant.firstName,
        tempLastName: participant.lastName,
        tempEmail: participant.email,
        ...mappedPhoneNumbers,
        tempCity: participant.city || '',
        tempStreet1: participant.streetAddress || '',
        tempStreet2: participant.streetAddress2 || null,
        tempZip: participant.zipCode || '',
        tempCountryCode: countryCode || '', // Use empty string instead of defaulting to 'US'
        tempCountry: participant.country || '', // Use empty string instead of defaulting to 'United States'
        isPrimary: participant.isPrimaryParticipant || false, // Use isPrimaryParticipant to match working code
        affiliation: participant.affiliation || '',
        createContact: true, // Create new contact - matches working code
        state: participant.state || '' // Use state to match other createParticipant calls
      };

      return this.leadsService.createParticipant(
        this.currentLeadId!,
        participantData
      ).toPromise();
    });

    // Execute all operations
    Promise.all([...deletePromises, ...updatePromises, ...createPromises])
      .then((results) => {
        // Reload lead to get updated participants
        if (this.currentLeadId) {
          setTimeout(() => {
            this.loadLeadById(this.currentLeadId!);
          }, 500);
        }

        this.presentActionResult({
          action: 'updated',
          message: 'Contact information updated successfully',
          timestamp: new Date(),
          isCustomMessage: true
        });
      })
      .catch(error => {
        console.error('Error saving participants:', error);
        this.presentActionResult({
          action: 'error',
          message: 'Error saving contact information. Please try again.',
          timestamp: new Date(),
          isCustomMessage: true,
          isError: true
        });
      });
  }

  onCardCancelClick(): void {
    // Just cancel, no action needed
    // The card will handle resetting its own state
  }

  private presentActionResult(result: ActionResult): void {
    presentActionResultWithActionFeedback(this.actionFeedback, result, { allowWrap: true });
  }
}

